import { Component, computed, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Tag } from '@models/common/tag';
import { ManageTagsGridService } from "@pages/settings/services/manage-tags-grid.service";
import { CommonPageComponent } from "@shared/components/commons/common-page/common-page.component";
import { LoaderComponent } from '@shared/components/commons/loader/loader.component';
import {
  AgManageTagsActionsComponent
} from '@shared/components/grid/ag-cells/settings/ag-manage-tags-actions/ag-manage-tags-actions.component';
import { BaseListGridComponent } from "@shared/components/grid/components/base-list-grid/base-list-grid.component";
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";
import { ButtonComponent } from '@shared/controls/button/button.component';
import { SearchInputComponent } from '@shared/controls/search-input/search-input.component';
import { HasRoleDirective } from '@shared/directives/has-role.directive';
import { IPartialHeaderData } from "@shared/services/header.service";
import { PageSizeService } from '@shared/services/page-size.service';
import { TagsService } from '@shared/services/tags.service';
import { doNothing } from "@shared/utils/doNothing";
import { RowSelectionOptions } from 'ag-grid-community';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, Observable, of, switchMap } from 'rxjs';

export const deleteTagMessage = 'Deleting the tag will permanently remove it from the available list and all associated companies.';
export const editTagMessage = 'The new tag name will be applied to all related entities.';

@Component({
  selector: 'app-manage-tags',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    SearchInputComponent,
    LoaderComponent,
    HasRoleDirective,
    ListGridClientSideComponent
  ],
  providers: [
    { provide: PageSizeService, useClass: PageSizeService },
    ManageTagsGridService
  ],
  templateUrl: './manage-tags-page.component.html',
  styleUrls: ['./manage-tags-page.component.scss', '../../styles/settings-styles.scss'],
})
export class ManageTagsPageComponent extends CommonPageComponent implements OnInit {
  private tagsService = inject(TagsService);
  private manageTagsGridService = inject(ManageTagsGridService);
  private toastr = inject(ToastrService);
  pageSizeService = inject(PageSizeService);

  override readonly headerData: IPartialHeaderData = {
    title: 'Manage Tags',
    iconClass: 'ic-tags-20',
    backPath: ['/settings']
  };
  searchControl = new FormControl<string | null>(null);
  allTags = this.tagsService.allTags;
  searchControlSignal = toSignal(this.searchControl.valueChanges);
  filteredTagList = computed(() => {
    const searchValue = this.searchControlSignal()?.toLowerCase().trim();

    if (!searchValue) {
      return this.allTags();
    }

    return this.allTags().filter(tag => tag.name?.toLowerCase().includes(searchValue));
  });

  override ngOnInit(): void {
    super.ngOnInit();
    this.setColDefs();
    this.loadTags().subscribe();
  }

  addTag(): void {
    const { modalRef, instance } = this.tagsService.openCreateEditTagModal();
    instance.isEdit = false;

    modalRef.result.then(
      (newTag: Tag) => {
        this.openCreateOrEditTagModal(newTag);
      },
      doNothing);
  }

  editTag(tag: Tag): void {
    const { modalRef, instance } = this.tagsService.openCreateEditTagModal();
    instance.isEdit = true;
    instance.tag = tag;

    modalRef.result.then(
      (editedTag: Tag) => {
        this.openCreateOrEditTagModal(editedTag);
        this.toastr.info(editTagMessage);
      },
      doNothing
    );
  }

  deleteTag(tagToDelete: Tag): void {
    if (!confirm(deleteTagMessage))
      return;
    this.isLoading.set(true);
    this.tagsService.deleteTag(tagToDelete.tagId)
      .pipe(
        catchError((error) => {
          console.warn('[ManageTagsComponent] getTags: ', error);
          this.toastr.error(`Failed to delete "${ tagToDelete.name }" tag.`);
          return of([]);
        }),
        switchMap(() => this.loadTags())
      )
      .subscribe();
  }

  loadTags(): Observable<Tag[]> {
    this.isLoading.set(true);
    return this.tagsService.getTags().pipe(finalize(() => this.isLoading.set(false)));
  }

  openCreateOrEditTagModal(tag: Tag): void {
    this.isLoading.set(true);
    this.tagsService.createOrUpdateTag(tag, '', false)
      .pipe(
        catchError((error) => {
          console.warn('[ManageTagsComponent] getTags: ', error);
          this.toastr.error('Failed to load tags.');
          return of([]);
        }),
        switchMap(() => this.loadTags())
      )
      .subscribe(() => this.toastr.success('Data saved successfully', 'Success'));
  }

  // =====  Grid  =====

  readonly manageTagsRowSelection: RowSelectionOptions<any> = BaseListGridComponent.noCheckboxesRowSelection;

  colDefs = this.manageTagsGridService.colDefs;

  setColDefs(): void {
    this.manageTagsGridService.setColDefs({
      headerName: '',
      cellRendererParams: {
        edit: this.editTag.bind(this),
        delete: this.deleteTag.bind(this),
      },
      cellRenderer: AgManageTagsActionsComponent,
      width: 90,
    });
  }
}
